import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { SUBJECTS, CLASS_LEVELS, LESSON_TYPES } from "@/constants/options";
import { Star, ShieldCheck, GraduationCap } from "lucide-react";
import { toast } from "sonner";

export default function TeacherSearch() {
  const [teachers, setTeachers] = useState([]);
  const [favs, setFavs] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ subject: "", class_level: "", lesson_type: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (f.subject) params.subject = f.subject;
      if (f.class_level) params.class_level = f.class_level;
      if (f.lesson_type) params.lesson_type = f.lesson_type;
      const { data } = await api.get("/teachers", { params });
      setTeachers(data.teachers || []);
      const favRes = await api.get("/favorites");
      setFavs(new Set(favRes.data.teacher_ids || []));
    } catch (err) {
      toast.error("Öğretmenler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [f]);

  useEffect(() => { load(); }, [load]);

  const toggleFav = async (e, teacherId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (favs.has(teacherId)) {
        await api.delete(`/favorites/${teacherId}`);
        const s = new Set(favs); s.delete(teacherId); setFavs(s);
      } else {
        await api.post("/favorites", { teacher_id: teacherId });
        setFavs(new Set([...favs, teacherId]));
        toast.success("Favorilere eklendi");
      }
    } catch {
      toast.error("Bir hata oluştu");
    }
  };

  const clear = () => setF({ subject: "", class_level: "", lesson_type: "" });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="mb-10 reveal">
          <span className="text-xs uppercase tracking-widest text-neutral-500 font-medium">Keşfet</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter" data-testid="search-title">Öğretmen Ara</h1>
          <p className="mt-3 text-neutral-500 max-w-xl">Branş, sınıf seviyesi ve ders türüne göre öğretmenleri filtrele.</p>
        </div>

        <div className="grid md:grid-cols-4 gap-8">
          {/* Filters */}
          <aside className="md:col-span-1">
            <div className="border border-neutral-200 rounded-2xl p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <div className="text-xs uppercase tracking-widest text-neutral-500 font-medium">Filtreler</div>
                <button onClick={clear} className="text-xs text-neutral-500 hover:text-neutral-900 underline underline-offset-2" data-testid="clear-filters-btn">Tümünü Temizle</button>
              </div>

              <FilterGroup title="Branş">
                {SUBJECTS.map((s) => (
                  <FilterPill key={s} active={f.subject === s} onClick={() => setF({ ...f, subject: f.subject === s ? "" : s })} testid={`filter-subject-${s}`}>
                    {s}
                  </FilterPill>
                ))}
              </FilterGroup>

              <FilterGroup title="Sınıf">
                {CLASS_LEVELS.map((c) => (
                  <FilterPill key={c} active={f.class_level === c} onClick={() => setF({ ...f, class_level: f.class_level === c ? "" : c })} testid={`filter-class-${c}`}>
                    {c}
                  </FilterPill>
                ))}
              </FilterGroup>

              <FilterGroup title="Ders türü">
                {LESSON_TYPES.map((lt) => (
                  <FilterPill key={lt.value} active={f.lesson_type === lt.value} onClick={() => setF({ ...f, lesson_type: f.lesson_type === lt.value ? "" : lt.value })} testid={`filter-lesson-${lt.value}`}>
                    {lt.label}
                  </FilterPill>
                ))}
              </FilterGroup>
            </div>
          </aside>

          {/* Results */}
          <section className="md:col-span-3">
            {loading ? (
              <div className="text-neutral-500 text-sm">Yükleniyor...</div>
            ) : teachers.length === 0 ? (
              <div className="border border-dashed border-neutral-300 rounded-2xl p-16 text-center">
                <GraduationCap className="mx-auto text-neutral-400" size={32} />
                <div className="mt-4 font-display text-xl">Sonuç bulunamadı</div>
                <p className="mt-2 text-sm text-neutral-500">Filtreleri değiştirerek tekrar dene.</p>
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {teachers.map((t) => (
                  <TeacherCard key={t.user_id} t={t} isFav={favs.has(t.user_id)} onToggle={(e) => toggleFav(e, t.user_id)} />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </Layout>
  );
}

export function TeacherCard({ t, isFav, onToggle }) {
  return (
    <Link
      to={`/ogretmen/${t.user_id}`}
      className="group border border-neutral-200 rounded-2xl overflow-hidden bg-white hover:shadow-md transition-shadow"
      data-testid={`teacher-card-${t.user_id}`}
    >
      <div className="relative aspect-square bg-neutral-100">
        {t.photo_path ? (
          <img src={fileUrl(t.photo_path)} alt={t.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center text-neutral-400">
            <GraduationCap size={40} />
          </div>
        )}
        <button
          onClick={onToggle}
          data-testid={`fav-btn-${t.user_id}`}
          className={`absolute top-3 right-3 h-10 w-10 rounded-full grid place-items-center transition-colors ${
            isFav ? "bg-[color:var(--star)] text-neutral-900" : "bg-white/90 hover:bg-white text-neutral-700"
          }`}
          aria-label="Favori"
        >
          <Star size={18} fill={isFav ? "currentColor" : "none"} />
        </button>
        {t.verified && (
          <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[color:var(--brand)] text-white rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm shadow-blue-500/30">
            <ShieldCheck size={12} fill="white" /> Doğrulanmış
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="font-display text-lg font-semibold tracking-tight group-hover:text-[color:var(--brand)] transition-colors truncate">{t.name}</div>
        <div className="mt-1 text-sm text-slate-500 truncate">{t.university}</div>
        <div className="mt-1 text-xs text-slate-500">{t.age} yaşında</div>
      </div>
    </Link>
  );
}

function FilterGroup({ title, children }) {
  return (
    <div className="mb-6">
      <div className="text-xs font-semibold mb-2">{title}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}
function FilterPill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
        active ? "bg-[color:var(--brand)] text-white border-[color:var(--brand)] shadow-sm shadow-blue-500/30" : "bg-white text-slate-700 border-slate-300 hover:border-[color:var(--brand)] hover:text-[color:var(--brand)]"
      }`}
    >
      {children}
    </button>
  );
}
